import React, { useEffect } from 'react';
import {
  X,
  Play,
  Clock,
  Users,
  Star,
  BookOpen,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import './CoursePreview.scss';

interface Course {
  title: string;
  creator: string;
  source: string;
  modules: number;
  duration: string;
  color: string;
  tags?: string[];
  description?: string;
  difficulty?: string;
}

interface CoursePreviewProps {
  course: Course;
  isOpen: boolean;
  onClose: () => void;
  onEnroll: (course: Course) => void;
}

export const CoursePreview: React.FC<CoursePreviewProps> = ({
  course,
  isOpen,
  onClose,
  onEnroll,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sampleModules = [
    'Introduction to Core Concepts',
    'Fundamentals and Key Principles',
    'Practical Applications',
    'Advanced Techniques',
  ];

  return (
    <div className="course-preview-overlay" onClick={onClose}>
      <div className="course-preview-modal" onClick={e => e.stopPropagation()}>
        <div className="course-preview-header">
          <div className="back-info">Course Preview</div>
          <button className="preview-close-button" onClick={onClose}>
            <X size={16} />
            Close
          </button>
        </div>

        <div className="course-preview-content">
          {/* Course Header */}
          <div className="course-preview-header-section">
            <div className="course-preview-meta">
              <span className="course-preview-source-badge">
                {course.source}
              </span>
              <span className="course-preview-difficulty">
                {course.difficulty ? course.difficulty.charAt(0).toUpperCase() + course.difficulty.slice(1) : 'Beginner'}
              </span>
            </div>

            <h1 className="course-preview-title">{course.title}</h1>
            <p className="course-preview-description">
              {course.description ||
                `An engaging course that covers all essential aspects of ${course.title.toLowerCase()}. Perfect for learners looking to gain comprehensive knowledge and practical skills.`}
            </p>

            <div className="course-preview-stats">
              <div className="course-preview-stat">
                <Clock size={16} />
                <span>{course.duration}</span>
              </div>
              <div className="course-preview-stat">
                <BookOpen size={16} />
                <span>{course.modules} lessons</span>
              </div>
              {course.difficulty && (
                <div className="course-preview-stat">
                  <Star size={16} />
                  <span>{course.difficulty}</span>
                </div>
              )}
            </div>

            {course.tags && (
              <div className="course-preview-tags">
                {course.tags.map((tag, index) => (
                  <span key={index} className="course-preview-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Course Content */}
          <div className="course-preview-lessons-section">
            <h2>Course Content</h2>
            <div className="course-preview-lessons-list">
              {sampleModules
                .slice(0, Math.min(6, course.modules))
                .map((module, index) => (
                  <div key={index} className="preview-lesson-item">
                    <div className="preview-lesson-number">
                      {index === 0 ? (
                        <Play size={14} />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <div className="preview-lesson-content">
                      <h3 className="preview-lesson-title">{module}</h3>
                      <div className="preview-lesson-meta">
                        <span>
                          {Math.ceil(
                            parseInt(course.duration) / course.modules
                          )}{' '}
                          min
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              <div className="course-preview-gradient-overlay"></div>
              {course.modules > 6 && (
                <div className="course-preview-more-modules">
                  <span>+ {course.modules - 6} more lessons</span>
                </div>
              )}
            </div>
          </div>

          {/* Course Actions */}
          <div className="course-preview-actions">
            <button
              className="preview-enroll-button"
              onClick={() => onEnroll(course)}
            >
              <Play size={20} />
              Enroll Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
